import { formatDateRange } from "./utils"

export const STATUS_OPTIONS = ["Active", "Draft", "Paused", "Expired", "Archived"]

export const COUNTRY_OPTIONS = ["Nigeria", "Ghana", "Kenya", "South Africa"]

// Cameroon and Zambia aren't live markets for this campaign type yet, but the
// backend lookups endpoint still returns them — strip them out client-side
// wherever that list is rendered.
const EXCLUDED_COUNTRY_NAMES = ["cameroon", "zambia"]

export const filterOutExcludedCountries = (countries = []) =>
  countries.filter(c => !EXCLUDED_COUNTRY_NAMES.includes((c.name ?? "").toLowerCase()))

export const BRAND_OPTIONS = ["BetYAKATA Sports", "BetYAKATA Casino"]

export const AFFILIATE_OPTIONS = ["GoalPro Media", "KwesiBets", "SafariTips", "BetMzansi"]

// value must match the bonus_type enum accepted by POST /admin/bonus-campaigns.
// Only "bonus_balance" (Fixed Cash) has been confirmed against the real API so far —
// the other three are best-guess snake_case pending confirmation.
export const BONUS_TYPE_OPTIONS = [
  { label: "Fixed Cash", value: "bonus_balance" },
  { label: "Free Bet", value: "free_bet" },
  { label: "Deposit Match", value: "deposit_match" },
  { label: "Percentage", value: "percentage" },
]

export const bonusTypeLabelFromValue = value =>
  BONUS_TYPE_OPTIONS.find(o => o.value === value)?.label ?? value

export const CURRENCY_OPTIONS = ["NGN", "GHS", "KES", "ZAR", "USD"]

export const CURRENCY_SYMBOLS = { NGN: "₦", GHS: "₵", KES: "KSh ", ZAR: "R", USD: "$" }

// value must be a real IANA timezone accepted by the API (confirmed for South Africa
// via a live test: "Africa/Johannesburg").
export const TIMEZONE_OPTIONS = [
  { label: "(UTC+01:00) West Africa Time", value: "Africa/Lagos" },
  { label: "(UTC+00:00) GMT", value: "Africa/Accra" },
  { label: "(UTC+02:00) South Africa Standard Time", value: "Africa/Johannesburg" },
  { label: "(UTC+03:00) East Africa Time", value: "Africa/Nairobi" },
]

export const timezoneLabelFromValue = value =>
  TIMEZONE_OPTIONS.find(o => o.value === value)?.label ?? value

export const STATUS_META = {
  Active: { bg: "#e6f7f0", color: "#1f9d70", dot: "#34c38f" },
  Draft: { bg: "#eef0f4", color: "#74788d", dot: "#adb5bd" },
  Paused: { bg: "#fdf3e2", color: "#b6810f", dot: "#e6a635" },
  Expired: { bg: "#fdece0", color: "#c26a1b", dot: "#e08636" },
  Archived: { bg: "#eef0f4", color: "#8a94a6", dot: "#c2c8d2" },
}

export const AUDIT_STATUS_META = {
  Credited: { bg: "#e6f7f0", color: "#1f9d70", dot: "#34c38f" },
  Pending: { bg: "#fdf3e2", color: "#b6810f", dot: "#e6a635" },
  Failed: { bg: "#fde8e8", color: "#d0453f", dot: "#f46a6a" },
}

let campaigns = [
  {
    id: 1,
    name: "Affiliate Welcome Boost",
    subtitle: "Q3 Affiliate Acquisition",
    description: "Registration bonus for players referred by tier-1 affiliate partners during Q3.",
    status: "Active",
    bonusType: "Fixed Cash",
    currencyCode: "NGN",
    amount: "₦2,000",
    country: "Nigeria",
    affiliate: "GoalPro Media",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    createdDate: "2026-05-18",
    claims: 3842,
    remaining: 6158,
    maxClaims: 10000,
    lastUpdated: "2026-07-21",
    wageringRequirement: "x3",
    expiryDays: 14,
    minDeposit: "",
    maxBonusAmount: "",
    timezone: "(UTC+01:00) West Africa Time",
    eligibleCountries: ["Nigeria"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["GoalPro Media"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 2,
    name: "Ghana Signup Reward",
    subtitle: "GH Growth Q3",
    description: "Free bet reward for new sign-ups referred through Ghanaian affiliate partners.",
    status: "Active",
    bonusType: "Free Bet",
    currencyCode: "GHS",
    amount: "₵50",
    country: "Ghana",
    affiliate: "KwesiBets",
    startDate: "2026-06-15",
    endDate: "2026-09-15",
    createdDate: "2026-06-02",
    claims: 1275,
    remaining: 3725,
    maxClaims: 5000,
    lastUpdated: "2026-07-20",
    wageringRequirement: "x3",
    expiryDays: 10,
    minDeposit: "",
    maxBonusAmount: "",
    timezone: "(UTC+00:00) GMT",
    eligibleCountries: ["Ghana"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["KwesiBets"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 3,
    name: "Kenya Kickoff Bonus",
    subtitle: "KE Launch",
    description: "Deposit-match launch bonus supporting our Kenyan market entry.",
    status: "Draft",
    bonusType: "Deposit Match",
    currencyCode: "KES",
    amount: "KSh 300",
    country: "Kenya",
    affiliate: "SafariTips",
    startDate: "2026-09-01",
    endDate: "2026-11-30",
    createdDate: "2026-06-20",
    claims: 0,
    remaining: 8000,
    maxClaims: 8000,
    lastUpdated: "2026-07-19",
    wageringRequirement: "x5",
    expiryDays: 14,
    minDeposit: "500",
    maxBonusAmount: "",
    timezone: "(UTC+03:00) East Africa Time",
    eligibleCountries: ["Kenya"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["SafariTips"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 4,
    name: "SA Rand Welcome",
    subtitle: "ZA Acquisition",
    description: "Standing welcome bonus for players referred by South African affiliate partners.",
    status: "Paused",
    bonusType: "Fixed Cash",
    currencyCode: "ZAR",
    amount: "R100",
    country: "South Africa",
    affiliate: "BetMzansi",
    startDate: "2026-05-10",
    endDate: "2026-11-10",
    createdDate: "2026-04-28",
    claims: 2109,
    remaining: 9891,
    maxClaims: 12000,
    lastUpdated: "2026-07-15",
    wageringRequirement: "x4",
    expiryDays: 21,
    minDeposit: "50",
    maxBonusAmount: "",
    timezone: "(UTC+02:00) South Africa Standard Time",
    eligibleCountries: ["South Africa"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["BetMzansi"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 5,
    name: "Naija Mega Referral",
    subtitle: "NG Referral Push",
    description: "High-value referral push that ran through the end of last year.",
    status: "Expired",
    bonusType: "Fixed Cash",
    currencyCode: "NGN",
    amount: "₦5,000",
    country: "Nigeria",
    affiliate: "NaijaOdds",
    startDate: "2025-10-01",
    endDate: "2025-12-31",
    createdDate: "2025-09-15",
    claims: 6720,
    remaining: 0,
    maxClaims: 6720,
    lastUpdated: "2026-01-02",
    wageringRequirement: "x3",
    expiryDays: 14,
    minDeposit: "",
    maxBonusAmount: "",
    timezone: "(UTC+01:00) West Africa Time",
    eligibleCountries: ["Nigeria"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["NaijaOdds"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 6,
    name: "Free Bet Friday (Affiliate)",
    subtitle: "Weekly FB",
    description: "Recurring weekly free bet distributed across all active affiliate partners.",
    status: "Archived",
    bonusType: "Free Bet",
    currencyCode: "NGN",
    amount: "₦1,000 FB",
    country: "Nigeria",
    affiliate: "Multiple",
    startDate: "2026-01-01",
    endDate: "2026-06-30",
    createdDate: "2025-12-25",
    claims: 540,
    remaining: 19460,
    maxClaims: 20000,
    lastUpdated: "2026-07-01",
    wageringRequirement: "x1",
    expiryDays: 7,
    minDeposit: "",
    maxBonusAmount: "",
    timezone: "(UTC+01:00) West Africa Time",
    eligibleCountries: ["Nigeria"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: [...AFFILIATE_OPTIONS],
    toggles: { newPlayersOnly: false, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 7,
    name: "Diaspora Welcome",
    subtitle: "Intl Acquisition",
    description: "USD-denominated welcome bonus targeting diaspora players referred internationally.",
    status: "Active",
    bonusType: "Fixed Cash",
    currencyCode: "USD",
    amount: "$10",
    country: "Nigeria",
    affiliate: "GlobalAff",
    startDate: "2026-04-05",
    endDate: "2026-10-05",
    createdDate: "2026-03-20",
    claims: 812,
    remaining: 2188,
    maxClaims: 3000,
    lastUpdated: "2026-07-18",
    wageringRequirement: "x5",
    expiryDays: 30,
    minDeposit: "",
    maxBonusAmount: "",
    timezone: "(UTC+00:00) GMT",
    eligibleCountries: ["Nigeria", "Ghana", "Kenya", "South Africa"],
    eligibleBrands: ["BetYAKATA Sports"],
    eligibleAffiliates: ["GlobalAff"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
  {
    id: 8,
    name: "VIP Affiliate Boost",
    subtitle: "High Value Q3",
    description: "Deposit-match bonus reserved for high-value players referred by elite affiliate partners.",
    status: "Active",
    bonusType: "Deposit Match",
    currencyCode: "NGN",
    amount: "₦10,000",
    country: "Nigeria",
    affiliate: "EliteTips",
    startDate: "2026-06-01",
    endDate: "2026-08-31",
    createdDate: "2026-05-15",
    claims: 214,
    remaining: 786,
    maxClaims: 1000,
    lastUpdated: "2026-07-21",
    wageringRequirement: "x6",
    expiryDays: 30,
    minDeposit: "100",
    maxBonusAmount: "15,000",
    timezone: "(UTC+01:00) West Africa Time",
    eligibleCountries: ["Nigeria"],
    eligibleBrands: ["BetYAKATA Sports", "BetYAKATA Casino"],
    eligibleAffiliates: ["EliteTips"],
    toggles: { newPlayersOnly: true, onePerPlayer: true, allowSelfReferral: false },
  },
]

export const getCampaigns = () => campaigns

export const getCampaignById = id => campaigns.find(c => String(c.id) === String(id))

export const getAffiliateOptions = () => [...new Set(campaigns.map(c => c.affiliate))].sort()

export const addCampaign = campaign => {
  const id = campaigns.length ? Math.max(...campaigns.map(c => c.id)) + 1 : 1
  campaigns = [{ id, claims: 0, ...campaign }, ...campaigns]
  return campaigns
}

const toTitleCase = s =>
  typeof s === "string" && s.length ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s

// Maps the response of POST /admin/bonus-campaigns (or a single row of a future
// GET listing) into the shape this page's UI already renders.
export const mapCampaignApiResponse = (raw = {}) => {
  const amountNumber = Number(raw.amount) || 0
  const claims = Number(raw.claims_count ?? raw.claims ?? 0) || 0
  const maxClaims = Number(raw.max_claims) || 0
  const countryNames = (raw.countries ?? []).map(c => c.name)
  const brandNames = (raw.brands ?? []).map(b => b.name)
  // /admin/affiliates has no display-name field — affiliates are identified
  // by their referral code, not a company/person name.
  const affiliateNames = (raw.eligible_affiliates ?? []).map(
    a => a.username ?? a.referral_code ?? a.phone_number ?? `Affiliate #${a.id}`
  )

  return {
    id: raw.id,
    name: raw.bonus_name ?? raw.name,
    subtitle: raw.name,
    description: raw.description ?? "",
    status: toTitleCase(raw.status ?? raw.effective_status ?? "draft"),
    bonusType: bonusTypeLabelFromValue(raw.bonus_type),
    currencyCode: raw.currency,
    amount: `${CURRENCY_SYMBOLS[raw.currency] ?? ""}${amountNumber.toLocaleString()}`,
    country: countryNames.length > 1 ? "Multiple" : countryNames[0] ?? "—",
    affiliate: affiliateNames.length === 0 ? "—" : affiliateNames.length > 1 ? "Multiple" : affiliateNames[0],
    startDate: (raw.start_date ?? "").slice(0, 10),
    endDate: (raw.end_date ?? "").slice(0, 10),
    createdDate: (raw.created_at ?? "").slice(0, 10),
    claims,
    remaining: Math.max(0, maxClaims - claims),
    maxClaims,
    lastUpdated: (raw.updated_at ?? "").slice(0, 10),
    wageringRequirement: raw.wagering_requirement_multiplier
      ? `x${Number(raw.wagering_requirement_multiplier)}`
      : "",
    expiryDays: raw.expiry_days ?? "",
    minDeposit: raw.min_deposit ?? "",
    maxBonusAmount: raw.max_bonus_amount ?? "",
    timezone: timezoneLabelFromValue(raw.timezone),
    eligibleCountries: countryNames,
    eligibleBrands: brandNames,
    eligibleAffiliates: affiliateNames,
    toggles: {
      newPlayersOnly: true,
      onePerPlayer: !!raw.one_per_player,
      allowSelfReferral: !!raw.allow_self_referral,
    },
  }
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

// Maps the `trend` + `trend_total` fields of GET /admin/bonus-campaigns/:id/details
// into the {days, total} shape BonusTrendChart renders.
export const mapTrendResponse = (trend = [], trendTotal = 0) => ({
  days: trend.map(t => {
    const d = new Date(t.date)
    return { day: isNaN(d) ? t.date : WEEKDAY_LABELS[d.getUTCDay()], value: Number(t.count) || 0 }
  }),
  total: Number(trendTotal) || 0,
})

const formatPctDelta = (value, suffix) => {
  if (value === null || value === undefined) return "—"
  const rounded = Math.round(value)
  return `${rounded >= 0 ? "↑" : "↓"} ${Math.abs(rounded)}%${suffix}`
}

// Maps the `stats` object of GET /admin/bonus-campaigns/:id/details into the
// 7 stat tiles the campaign details view renders. Takes the already-mapped
// campaign (via mapCampaignApiResponse) for its amount/status/date display values.
export const mapCampaignStatsResponse = (stats = {}, campaign = {}) => {
  const currencySymbol = (campaign.amount || "").replace(/[0-9.,]+/g, "").trim()
  const totalValue = `${currencySymbol}${Number(stats.total_bonus_value || 0).toLocaleString()}`

  return [
    {
      label: "Total Registrations",
      value: Number(stats.total_registrations || 0).toLocaleString(),
      delta: formatPctDelta(stats.total_registrations_change_pct, " vs last week"),
      deltaClass: (stats.total_registrations_change_pct ?? 0) >= 0 ? "text-success" : "text-danger",
    },
    {
      label: "Eligible Registrations",
      value: Number(stats.eligible_registrations || 0).toLocaleString(),
      delta: stats.eligible_registrations_pct != null ? `${Math.round(stats.eligible_registrations_pct)}% of total` : "—",
      deltaClass: "text-muted",
    },
    {
      label: "Bonuses Awarded",
      value: Number(stats.bonuses_awarded || 0).toLocaleString(),
      delta: `${campaign.amount} each`,
      deltaClass: "text-muted",
    },
    {
      label: "Failed Awards",
      value: Number(stats.failed_awards || 0).toLocaleString(),
      delta: formatPctDelta(stats.failed_awards_change_pct, " vs last week"),
      deltaClass: (stats.failed_awards_change_pct ?? 0) <= 0 ? "text-success" : "text-danger",
    },
    {
      label: "Total Bonus Value",
      value: totalValue,
      delta: "awarded to date",
      deltaClass: "text-muted",
    },
    {
      label: "Conversion Rate",
      value: stats.conversion_rate != null ? `${Math.round(stats.conversion_rate)}%` : "—",
      delta: "reg → first bet",
      deltaClass: "text-muted",
    },
    {
      label: "Campaign Status",
      value: toTitleCase(stats.campaign_status ?? campaign.status),
      delta: formatDateRange(campaign.startDate, campaign.endDate),
      deltaClass: "text-muted",
    },
  ]
}

// Maps the `recent_awards` rows of GET /admin/bonus-campaigns/:id/details into
// the shape the "Recent Awards" table renders.
export const mapRecentAwardsResponse = (rows = []) =>
  rows.map((r, i) => {
    const amountNumber = Number(r.amount) || 0
    return {
      id: r.id ?? i,
      player: r.player_name ?? r.player ?? r.username ?? "—",
      amount: `${CURRENCY_SYMBOLS[r.currency] ?? ""}${amountNumber.toLocaleString()}`,
      awardDate: r.awarded_at ?? r.created_at ?? r.award_date ?? "",
      status: toTitleCase(r.status ?? "pending"),
    }
  })

export const updateCampaign = (id, changes) => {
  campaigns = campaigns.map(c => (String(c.id) === String(id) ? { ...c, ...changes } : c))
  return campaigns
}

export const deleteCampaign = id => {
  campaigns = campaigns.filter(c => String(c.id) !== String(id))
  return campaigns
}

// credited/pending map straight to their title-cased label; rejected and
// expired both read as "Failed" to the admin reviewing the audit log.
const AWARD_STATUS_LABELS = {
  credited: "Credited",
  pending: "Pending",
  rejected: "Failed",
  expired: "Failed",
}

const humanizeReason = reason =>
  typeof reason === "string" && reason ? reason.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : ""

// Maps a row of GET /admin/bonus-awards into the shape AuditLogs/AuditDrawer render.
export const mapBonusAwardApiResponse = (raw = {}) => {
  const user = raw.user ?? {}
  const referrer = raw.referrer ?? {}
  const campaign = raw.campaign ?? {}
  const amountNumber = Number(raw.amount) || 0
  const currencySymbol = CURRENCY_SYMBOLS[campaign.currency] ?? ""

  return {
    id: raw.id,
    player: user.display_name ?? user.name ?? user.username ?? user.phone_number ?? `Player #${user.id}`,
    playerId: user.phone_number ? `#${user.phone_number}` : `#${user.id}`,
    username: user.username ?? "—",
    affiliate:
      referrer.referral_code ?? referrer.display_name ?? referrer.name ?? referrer.username ?? `Affiliate #${referrer.id}`,
    referralCode: referrer.referral_code ?? "—",
    campaignId: campaign.id,
    campaign: campaign.bonus_name ?? campaign.name ?? "—",
    amount: `${currencySymbol}${amountNumber.toLocaleString()}`,
    awardDate: raw.award_date ?? raw.created_at ?? "",
    status: AWARD_STATUS_LABELS[raw.status] ?? toTitleCase(raw.status),
    reason: humanizeReason(raw.rejection_reason),
  }
}

const WEEKLY_TREND_BASE = [62, 88, 74, 96, 120, 142, 108]
const WEEKLY_TREND_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

export const getWeeklyTrend = campaign => {
  const scale = Math.max(0.15, Math.min(1.4, campaign.claims / 3800))
  const values = WEEKLY_TREND_BASE.map(v => Math.max(1, Math.round(v * scale)))
  return {
    days: WEEKLY_TREND_DAYS.map((day, i) => ({ day, value: values[i] })),
    total: values.reduce((sum, v) => sum + v, 0),
  }
}

export const computeCampaignStats = campaign => {
  const registrations = Math.round(campaign.claims * 1.34)
  const eligibleRegistrations = Math.round(campaign.claims * 1.08)
  const failedAwards = Math.round(campaign.claims * 0.03)
  const amountNumber = parseFloat((campaign.amount || "").replace(/[^0-9.]/g, "")) || 0
  const currencySymbol = (campaign.amount || "").replace(/[0-9.,]+/g, "").trim()
  const totalValue = `${currencySymbol}${(amountNumber * campaign.claims).toLocaleString()}`
  const conversionRate = Math.min(95, 55 + (campaign.claims % 30))

  return [
    { label: "Total Registrations", value: registrations.toLocaleString(), delta: "↑ 12% vs last week", deltaClass: "text-success" },
    { label: "Eligible Registrations", value: eligibleRegistrations.toLocaleString(), delta: registrations ? `${Math.round((eligibleRegistrations / registrations) * 100)}% of total` : "—", deltaClass: "text-muted" },
    { label: "Bonuses Awarded", value: campaign.claims.toLocaleString(), delta: `${campaign.amount} each`, deltaClass: "text-muted" },
    { label: "Failed Awards", value: failedAwards.toLocaleString(), delta: "↓ 2% vs last week", deltaClass: "text-success" },
    { label: "Total Bonus Value", value: totalValue, delta: "awarded to date", deltaClass: "text-muted" },
    { label: "Conversion Rate", value: `${conversionRate}%`, delta: "reg → first bet", deltaClass: "text-muted" },
    { label: "Campaign Status", value: campaign.status, delta: `${campaign.startDate} – ${campaign.endDate}`, deltaClass: "text-muted" },
  ]
}
